import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useSelector, useDispatch } from 'react-redux';
import { getUser } from '../../redux/user/actions';
import types from '../../redux/types';

import API from '../../services/api';
import components from '../../components';
import utils from '../../utils';

import './styles.scss';

const { Profile } = components;
const { UserProfile, HelpfulLinks, Preferences, APIKeys } = Profile;

const ProfileContainer = ({ documentationEnabled }) => {
  const user = useSelector(getUser);
  const dispatch = useDispatch();
  const [apiKeys, setAPIKeys] = useState([]);
  const [nextAPIKeysPageToken, setNextAPIKeysPageToken] = useState('');
  const [isAPIKeysLoading, setIsAPIKeysLoading] = useState(false);
  const apiKeysRequestVersion = useRef(0);

  const loadAPIKeys = useCallback(async (pageToken = '', append = false) => {
    const requestVersion = ++apiKeysRequestVersion.current;
    setIsAPIKeysLoading(true);
    try {
      const response = await API.getAPIKeys(pageToken);
      if (requestVersion !== apiKeysRequestVersion.current) {
        return;
      }
      const newKeys = response.apiKeys || [];
      setAPIKeys(currentKeys => {
        if (!append) {
          return newKeys;
        }
        const knownKeyIDs = new Set(currentKeys.map(apiKey => apiKey.id));
        return [...currentKeys, ...newKeys.filter(apiKey => !knownKeyIDs.has(apiKey.id))];
      });
      setNextAPIKeysPageToken(response.nextPageToken || '');
    } catch (error) {
      if (requestVersion === apiKeysRequestVersion.current) {
        dispatch({
          type: types.SET_ALERT,
          payload: utils.generateAlert(error.message, 'error')
        });
      }
    } finally {
      if (requestVersion === apiKeysRequestVersion.current) {
        setIsAPIKeysLoading(false);
      }
    }
  }, [dispatch]);

  useEffect(() => {
    apiKeysRequestVersion.current += 1;
    setAPIKeys([]);
    setNextAPIKeysPageToken('');
    if (user) {
      loadAPIKeys();
    } else {
      setIsAPIKeysLoading(false);
    }
  }, [loadAPIKeys, user?.id]);

  const createCurrentUserAPIKey = async apiKey => {
    const response = await API.createAPIKey(apiKey);
    apiKeysRequestVersion.current += 1;
    setIsAPIKeysLoading(false);
    setAPIKeys(currentKeys => [response.apiKey, ...currentKeys]);
    dispatch({
      type: types.SET_ALERT,
      payload: utils.generateAlert('API key has been created.', 'success', 2000)
    });
    return response;
  };

  const revokeCurrentUserAPIKey = async keyId => {
    await API.deleteAPIKey(keyId);
    apiKeysRequestVersion.current += 1;
    setIsAPIKeysLoading(false);
    setAPIKeys(currentKeys => currentKeys.filter(apiKey => apiKey.id !== keyId));
    dispatch({
      type: types.SET_ALERT,
      payload: utils.generateAlert('API key has been revoked.', 'success', 2000)
    });
  };

  const updateUser = (userData, fields, cb) => {
    API.updateCurrentUser(userData, fields)
      .then(() => {
        cb();
        dispatch({
          type: types.SET_ALERT,
          payload: utils.generateAlert('User has been updated successfuly', 'success')
        });
      })
      .catch(error => {
        cb(error);
        dispatch({
          type: types.SET_ALERT,
          payload: utils.generateAlert(error.message, 'error')
        });
      });
  };

  if (!user) {
    return null;
  }

  return (
    <div className={`profile-container ${user?.type === 'BRAND' ? 'is-brand' : ''}`}>
      <div className="profile-column">
        <UserProfile user={user} onChange={updateUser} />
        <APIKeys
          apiKeys={apiKeys}
          hasMore={Boolean(nextAPIKeysPageToken)}
          isLoading={isAPIKeysLoading}
          onCreate={createCurrentUserAPIKey}
          onLoadMore={() => loadAPIKeys(nextAPIKeysPageToken, true)}
          onRevoke={revokeCurrentUserAPIKey}
        />
        <HelpfulLinks documentationEnabled={documentationEnabled} />
      </div>
      {user.type !== 'BRAND' && <Preferences onChange={updateUser} />}
    </div>
  );
};

export default ProfileContainer;
